"use client";

import React from "react";
import { businessData } from "@/data/business";

export type AnnouncementIcon = "shield" | "clock" | "box";

export interface AnnouncementItem {
  text: string;
  boldText?: string;
  bold?: string;
  icon?: AnnouncementIcon | string;
}

export interface AnnouncementBarSettings {
  enabled?: boolean;
  items?: AnnouncementItem[];
  speedSeconds?: number;
  pauseOnHover?: boolean;
}

const defaultItems: AnnouncementItem[] = businessData.topBarTicker.map((item) => ({
  text: item.text,
  boldText: item.bold,
  icon: item.icon,
}));

function AnnouncementIconSvg({ icon }: { icon?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="top-bar-icon"
      aria-hidden="true"
    >
      {icon === "clock" ? (
        <>
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 16 14 16" />
        </>
      ) : icon === "box" ? (
        <>
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
          <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
          <line x1="12" y1="22.08" x2="12" y2="12" />
        </>
      ) : (
        <>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <polyline points="9 12 11 14 15 10" />
        </>
      )}
    </svg>
  );
}

function AnnouncementGroup({ items, hidden }: { items: AnnouncementItem[]; hidden?: boolean }) {
  return (
    <span className="top-bar-group" aria-hidden={hidden || undefined}>
      {items.map((item, index) => {
        const boldText = item.boldText || item.bold || "";
        const remainder = boldText ? item.text.replace(boldText, "").trim() : item.text;

        return (
          <React.Fragment key={`${item.text}-${index}`}>
            <span className="top-bar-item">
              <AnnouncementIconSvg icon={item.icon} />
              <span>
                {boldText && <strong>{boldText}</strong>}
                {boldText && remainder && " "}
                {remainder}
              </span>
            </span>
            <span className="top-bar-sep" aria-hidden="true">•</span>
          </React.Fragment>
        );
      })}
    </span>
  );
}

export function AnnouncementBar({
  enabled = true,
  items = defaultItems,
  speedSeconds = 24,
  pauseOnHover = true,
}: AnnouncementBarSettings) {
  const safeItems = items.filter((item) => item?.text?.trim());
  if (!enabled || safeItems.length === 0) return null;

  const duration = Math.min(90, Math.max(12, Number(speedSeconds) || 24));

  return (
    <aside
      className={`top-bar${pauseOnHover ? " top-bar--pausable" : ""}`}
      aria-label="Shaaira Biocare announcements"
    >
      <div
        className="top-bar-track"
        style={{ "--marquee-duration": `${duration}s` } as React.CSSProperties}
      >
        <AnnouncementGroup items={safeItems} />
        <AnnouncementGroup items={safeItems} hidden />
      </div>
    </aside>
  );
}

export const defaultAnnouncementBarSettings: AnnouncementBarSettings = {
  enabled: true,
  items: defaultItems,
  speedSeconds: 24,
  pauseOnHover: true,
};
